import { useCallback, useEffect, useState } from 'react'
import { Check, X, Send, Archive, Megaphone, Palette } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { Textarea } from '../../lib/shadcn/textarea'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { Chip } from '../interviews/Chip'
import { AssetStrip } from './AssetStrip'
import { UploadButton } from './UploadButton'
import { CaptionOptions } from './CaptionOptions'
import { GuardrailNotice } from './GuardrailNotice'
import { useReopenConfirm } from './ReopenWarning'
import {
  useArchiveContentItem,
  useDecideContentItem,
  useGetContentItem,
  usePublishContentItem,
  useSubmitContentItem,
  useUpdateContentItem,
} from '../../hooks/backend/marketing'
import { writeErrorMessage } from '../../utils/writeError'
import {
  allFlagsAcknowledged,
  approvedFieldsIn,
  historyFieldLabel,
  historyValue,
  rowActions,
  shortDate,
  waitedFor,
  STATE_TONES,
  type CodeLabel,
  type ContentHistoryRow,
  type ContentReview,
  type ContentRow,
  type DesignRequestRow,
  type MarketingPlan,
} from '../../utils/marketing'

function ReviewTrail({ reviews }: { reviews: ContentReview[] }) {
  if (reviews.length === 0) {
    return <p className="text-xs text-muted-foreground">Not submitted for approval yet.</p>
  }
  return (
    <ol className="space-y-2">
      {reviews.map((r) => (
        <li key={r.id} className="rounded-md border border-border bg-background p-2.5">
          <div className="flex flex-wrap items-center gap-1.5">
            <Chip
              tone={
                r.decision === 'Approved'
                  ? 'green'
                  : r.decision === 'Changes requested'
                    ? 'red'
                    : r.decision === 'Withdrawn' || r.decision === 'Reopened'
                      ? 'neutral'
                      : 'amber'
              }
            >
              {r.decision}
            </Chip>
            <span className="text-xs text-foreground">{r.actor_name ?? r.actor_email}</span>
            <span className="ml-auto text-[11px] text-muted-foreground">{shortDate(r.at)}</span>
          </div>
          {/* What the decision covered. An approval that cannot say which files
              it was about is not an approval, and this is where that promise is
              cashed. */}
          <p className="mt-1 text-[11px] text-muted-foreground">
            {r.asset_count === 0
              ? 'No files attached at the time'
              : `${r.asset_count} file${r.asset_count === 1 ? '' : 's'} at the time`}
          </p>
          {/* The caption as it stood at that moment. An approval that cannot
              show the sentence it approved is not a record, and an option can be
              rewritten after it was sent back. */}
          {r.caption_snapshot && (
            <p className="mt-1.5 rounded border border-border bg-muted/40 p-1.5 text-[11px] italic text-muted-foreground">
              {r.caption_snapshot}
            </p>
          )}
          {r.guardrail_flags?.length > 0 && (
            <p className="mt-1 text-[11px] text-amber-700 dark:text-amber-300">
              {r.guardrail_flags.length} guardrail
              {r.guardrail_flags.length === 1 ? '' : 's'} flagged,{' '}
              {r.acknowledged_codes?.length ?? 0} confirmed at the time
            </p>
          )}
          {r.note && <p className="mt-1.5 whitespace-pre-wrap text-xs text-foreground">{r.note}</p>}
        </li>
      ))}
    </ol>
  )
}

/**
 * Every edit to the post, newest first: who, when, which field, from what to
 * what. Collapsed to the latest ten, because the question it answers is usually
 * "what changed since I last looked", and the whole list is one click away.
 */
function EditHistory({ rows }: { rows: ContentHistoryRow[] }) {
  const [all, setAll] = useState(false)
  if (rows.length === 0) {
    return <p className="text-xs text-muted-foreground">No edits recorded yet.</p>
  }
  const shown = all ? rows : rows.slice(0, 10)
  return (
    <div className="space-y-1.5">
      <ol className="space-y-1.5">
        {shown.map((h) => (
          <li key={h.id} className="rounded border border-border bg-background px-2 py-1.5">
            <div className="flex flex-wrap items-baseline gap-1.5">
              <span className="text-[11px] font-medium text-foreground">{historyFieldLabel(h.field)}</span>
              <span className="text-[11px] text-muted-foreground">{h.actor_name ?? h.actor_email}</span>
              <span className="ml-auto text-[11px] text-muted-foreground">{shortDate(h.at)}</span>
            </div>
            <p className="mt-0.5 line-clamp-3 whitespace-pre-wrap text-[11px] text-muted-foreground">
              <span className="line-through decoration-muted-foreground/60">{historyValue(h.old_value)}</span>
              {'  to  '}
              <span className="text-foreground">{historyValue(h.new_value)}</span>
            </p>
          </li>
        ))}
      </ol>
      {rows.length > 10 && (
        <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setAll((a) => !a)}>
          {all ? 'Show the latest ten' : `Show all ${rows.length} edits`}
        </Button>
      )}
    </div>
  )
}

export function ContentPanel({
  itemId,
  channels,
  formats,
  audiences,
  ctas,
  plans,
  canEditMarketing,
  canApproveContent,
  onClose,
  onChanged,
  onRequestDesign,
}: {
  itemId: string
  channels: CodeLabel[]
  formats: CodeLabel[]
  audiences: CodeLabel[]
  ctas: CodeLabel[]
  plans: MarketingPlan[]
  canEditMarketing: boolean
  canApproveContent: boolean
  onClose: () => void
  onChanged: () => void
  onRequestDesign: (item: ContentRow) => void
}) {
  const detail = useGetContentItem()
  const update = useUpdateContentItem()
  const submit = useSubmitContentItem()
  const decide = useDecideContentItem()
  const publish = usePublishContentItem()
  const archive = useArchiveContentItem()

  const trigger = detail.trigger
  const load = useCallback(() => {
    void trigger({ id: itemId }, { skipCache: true })
  }, [trigger, itemId])

  useEffect(load, [load])

  const data = detail.data as
    | {
        item: ContentRow
        reviews: ContentReview[]
        designRequests: DesignRequestRow[]
        history?: ContentHistoryRow[]
      }
    | null
    | undefined

  const [note, setNote] = useState('')
  const [busy, setBusy] = useState<string | null>(null)
  // Which guardrail flags the approver has ticked. Held here and never
  // persisted between items: an acknowledgement is about one caption.
  const [acked, setAcked] = useState<string[]>([])
  // Bumped when an edit is abandoned at the reopen warning, which remounts the
  // uncontrolled fields so they show the stored value again and not the words
  // that were not saved.
  const [resetKey, setResetKey] = useState(0)
  const reopen = useReopenConfirm()

  // Cleared when the panel moves to another item, so a reason typed against one
  // piece of content, or a flag ticked against one caption, can never be
  // submitted against another.
  useEffect(() => {
    setNote('')
    setAcked([])
  }, [itemId])

  if (detail.loading && !data) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
        <Spinner className="h-4 w-4" /> Loading…
      </div>
    )
  }
  if (detail.error) {
    return (
      <div className="p-4">
        <p className="text-sm text-rose-700 dark:text-rose-300">{detail.error}</p>
        <Button size="sm" variant="outline" className="mt-3" onClick={onClose}>
          Close
        </Button>
      </div>
    )
  }
  if (!data) return null

  const item = data.item
  const can = rowActions(item, { canEditMarketing, canApproveContent })

  async function run(name: string, fn: () => Promise<unknown>, success: string) {
    setBusy(name)
    try {
      await fn()
      toast.success(success)
      setNote('')
      load()
      onChanged()
    } catch (err) {
      toast.error('That did not go through', { description: writeErrorMessage(err) })
    } finally {
      setBusy(null)
    }
  }

  async function saveField(patch: Record<string, unknown>) {
    const covered = can.reopensOnEdit ? approvedFieldsIn(patch) : []
    if (covered.length > 0) {
      const ok = await reopen.ask(covered)
      if (!ok) {
        setResetKey((k) => k + 1)
        return
      }
      return run(
        'field',
        () => update.trigger({ id: itemId, patch: { ...patch, confirm_reopen: true } }).result,
        'Saved. It is back to Draft and needs approving again.',
      )
    }
    return run('field', () => update.trigger({ id: itemId, patch }).result, 'Saved')
  }

  const hashtagText = (item.hashtags ?? []).join(' ')
  const sentBackReason =
    item.state === 'Changes requested' && item.last_decision === 'Changes requested'
      ? item.last_decision_note
      : null

  return (
    <div className="flex h-full min-h-0 flex-col bg-card">
      <div className="flex items-start gap-2 border-b border-border px-4 py-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] tabular-nums text-muted-foreground">#{item.ref}</span>
            <Chip tone={STATE_TONES[item.state]}>{item.state}</Chip>
            {item.task_ref !== null && (
              <span className="text-[11px] text-muted-foreground">task #{item.task_ref}</span>
            )}
          </div>
          <h2 className="mt-1 text-sm font-semibold text-foreground">{item.title}</h2>
          {item.awaiting_approval && item.days_waiting !== null && (
            <p className="mt-0.5 text-[11px] text-amber-700 dark:text-amber-300">
              With the approver since {shortDate(item.submitted_at)}, {waitedFor(item.days_waiting)}
            </p>
          )}
        </div>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          aria-label="Close"
          className="h-7 w-7 shrink-0 p-0"
          onClick={onClose}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {reopen.dialog}
      <div key={resetKey} className="min-h-0 flex-1 space-y-4 overflow-auto px-4 py-3">
        {/* Why it came back, in the approver's words, first. The writer opens this
            to fix something, and a reason three sections down in the review trail
            is a reason read after the wrong thing has been changed. */}
        {sentBackReason && (
          <div className="rounded-md border border-rose-300 bg-rose-50 p-2.5 dark:border-rose-800 dark:bg-rose-950/40">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-rose-800 dark:text-rose-200">
              Sent back for changes
            </p>
            <p className="mt-0.5 whitespace-pre-wrap text-xs text-foreground">{sentBackReason}</p>
          </div>
        )}
        {item.state === 'Submitted' && !can.canDecide && canEditMarketing && (
          <p className="rounded-md border border-border bg-muted/50 p-2.5 text-[11px] text-muted-foreground">
            With the approver. Nothing on it can change until they decide; if it has to change now,
            ask them to send it back.
          </p>
        )}
        {can.reopensOnEdit && (
          <p className="rounded-md border border-amber-300 bg-amber-50 p-2.5 text-[11px] text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
            Approved. Changing the title, audience, channel, format, design copy, call to action,
            hashtags or a caption sends it back to Draft for a new approval, and you will be asked
            first. The date and the notes can change without that.
          </p>
        )}

        <section className="space-y-1.5">
          <Label className="text-xs">Title</Label>
          <Input
            defaultValue={item.title}
            disabled={!can.canEditWording || busy !== null}
            className="h-9"
            onBlur={(e) => {
              if (!can.canEditWording) return
              if (item.title === e.target.value.trim() || e.target.value.trim() === '') return
              void saveField({ title: e.target.value })
            }}
          />
        </section>

        {/* The plan's own production note, first and unmissable. It is the
            instruction the whole guardrail exists to carry: "R1,200 is a target,
            not guaranteed pay", "reproduce approved rates exactly". Whoever is
            writing and whoever is signing both need it before anything else on
            this panel. */}
        {item.production_note && (
          <div className="rounded-md border border-border bg-muted/50 p-2.5">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              From the plan
            </p>
            <p className="mt-0.5 text-xs text-foreground">{item.production_note}</p>
            {item.source_basis && (
              <p className="mt-1 text-[11px] text-muted-foreground">
                Figures from: {item.source_basis}
              </p>
            )}
          </div>
        )}

        {/* THE DECISION, next. The approver opens this panel to decide, and a
            decision below three edit forms is a decision made after scrolling. */}
        {can.canDecide && (
          <section className="space-y-2 rounded-md border border-[hsl(var(--brand))]/40 bg-[hsl(var(--brand-soft))]/30 p-3">
            <p className="text-xs font-semibold text-foreground">Your decision</p>
            {item.submitted_with_no_file && (
              <p className="text-[11px] text-amber-700 dark:text-amber-300">
                Nothing is attached to this. For some channels a caption on its own is the whole
                piece, so it has not been refused, but it is worth checking before approving.
              </p>
            )}

            {/* The words being approved, quoted here rather than left three
                sections down. An approval made without the caption on screen is
                an approval of a title. */}
            {item.caption && (
              <div className="rounded border border-border bg-background p-2">
                <p className="text-[11px] text-muted-foreground">{item.caption_label}</p>
                <p className="mt-0.5 whitespace-pre-wrap text-xs text-foreground">{item.caption}</p>
              </div>
            )}
            {item.caption_not_chosen && (
              <p className="text-[11px] text-amber-700 dark:text-amber-300">
                No caption has been chosen out of the {item.caption_option_count} written, so there
                is nothing to approve yet. Choose one below, or send it back.
              </p>
            )}

            <GuardrailNotice
              flags={item.guardrail_flags ?? []}
              acknowledged={acked}
              onToggle={(code) =>
                setAcked((a) => (a.includes(code) ? a.filter((c) => c !== code) : [...a, code]))
              }
            />
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="What needs changing, or a note on the approval"
              className="text-sm"
            />
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                variant="brand"
                className="gap-1.5"
                // Disabled rather than refused after the click. The database
                // refuses it too, with the rule's own sentence; this is so the
                // person is not told off for something the screen could have
                // said first.
                disabled={busy !== null || !allFlagsAcknowledged(item, acked)}
                title={
                  allFlagsAcknowledged(item, acked)
                    ? undefined
                    : 'Tick each guardrail above once you have checked it'
                }
                onClick={() =>
                  void run(
                    'approve',
                    () =>
                      decide.trigger({
                        id: itemId,
                        decision: 'Approved',
                        note: note || null,
                        acknowledged_codes: acked,
                      }).result,
                    'Approved',
                  )
                }
              >
                {busy === 'approve' ? <Spinner className="h-3.5 w-3.5" /> : <Check className="h-3.5 w-3.5" />}
                Approve
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="gap-1.5"
                // Disabled without a reason rather than refused after the click.
                // The database refuses it too; this is so the person is not
                // told off for something the screen could have said first.
                disabled={busy !== null || note.trim() === ''}
                title={note.trim() === '' ? 'Say what needs changing first' : undefined}
                onClick={() =>
                  void run(
                    'reject',
                    () =>
                      decide.trigger({
                        id: itemId,
                        decision: 'Changes requested',
                        note,
                        // Not sent on a rejection. Nothing is being approved, so
                        // there is nothing to have confirmed.
                        acknowledged_codes: [],
                      }).result,
                    'Sent back with your note',
                  )
                }
              >
                {busy === 'reject' ? <Spinner className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                Request changes
              </Button>
            </div>
            {note.trim() === '' && (
              <p className="text-[11px] text-muted-foreground">
                Sending work back needs a reason. Approving does not.
              </p>
            )}
            {!allFlagsAcknowledged(item, acked) && (
              <p className="text-[11px] text-muted-foreground">
                Approve unlocks once each guardrail above is ticked. Sending it back does not need
                them.
              </p>
            )}
          </section>
        )}

        <section className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-semibold text-foreground">Files</p>
            {can.canUpload && (
              <UploadButton contentItemId={itemId} onUploaded={() => { load(); onChanged() }} />
            )}
          </div>
          {!can.canUpload && canEditMarketing && (
            <p className="text-[11px] text-muted-foreground">
              {item.state === 'Approved'
                ? 'Approved, so the files are fixed. Ask the approver to send it back to change the artwork.'
                : item.state === 'Submitted'
                  ? 'With the approver, so the files cannot change until they decide.'
                  : item.state === 'Archived'
                    ? 'Stopped, so the files are kept as they were.'
                    : 'Published, so the files are the record of what went out.'}
            </p>
          )}
          <AssetStrip
            assets={item.assets ?? []}
            canRemove={can.canUpload}
            onChanged={() => {
              load()
              onChanged()
            }}
          />
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-7 gap-1.5 px-2 text-xs"
            onClick={() => onRequestDesign(item)}
          >
            <Palette className="h-3.5 w-3.5" />
            Ask for a design for this
          </Button>
          {data.designRequests.length > 0 && (
            <ul className="space-y-1">
              {data.designRequests.map((d) => (
                <li key={d.id} className="text-[11px] text-muted-foreground">
                  Design #{d.ref}, {d.status.toLowerCase()} with{' '}
                  {d.assigned_to_name ?? 'the designer'}
                  {d.overdue ? ', past its date' : ''}
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* THE COPY THAT GOES ON THE ARTWORK, which is not the caption. Turf
            sets this inside the image and Xolile writes the caption; the
            September documents keep them in separate files for that reason. */}
        <section className="space-y-2">
          <p className="text-xs font-semibold text-foreground">On the artwork</p>
          <Textarea
            defaultValue={item.design_copy ?? ''}
            rows={4}
            disabled={!can.canEditWording || busy !== null}
            placeholder="The words that appear in the design itself"
            className="font-mono text-xs"
            onBlur={(e) => {
              if (!can.canEditWording) return
              if ((item.design_copy ?? '') === e.target.value) return
              void saveField({ design_copy: e.target.value })
            }}
          />
          {!can.canEditWording && canEditMarketing && (
            <p className="text-[11px] text-muted-foreground">
              Fixed while this is {item.state.toLowerCase()}.
            </p>
          )}
        </section>

        <CaptionOptions
          item={item}
          ctas={ctas}
          canEdit={can.canEditCaptions}
          canChoose={can.canChooseCaption}
          reopensOnEdit={can.reopensOnEdit}
          confirmReopen={() => reopen.ask(['caption'])}
          onChanged={() => {
            load()
            onChanged()
          }}
        />

        {/* Shown to the writer as well, read-only, so the person who can still
            change the wording sees it before the approver does. */}
        {!can.canDecide && (item.guardrail_flags?.length ?? 0) > 0 && (
          <GuardrailNotice flags={item.guardrail_flags} />
        )}

        {/* What Turf is being asked to make. This is what a design request for
            this post is pre-filled from, so it is worth writing once here rather
            than retyping into every request. */}
        <section className="space-y-1.5">
          <Label className="text-xs">Creative direction, for the designer</Label>
          <Textarea
            defaultValue={item.creative_direction ?? ''}
            rows={2}
            disabled={!can.canEditNotes || busy !== null}
            placeholder="Premium white Suzuki Swift, Fleetly red, minimal copy"
            className="text-sm"
            onBlur={(e) => {
              if ((item.creative_direction ?? '') === e.target.value) return
              void saveField({ creative_direction: e.target.value })
            }}
          />
        </section>

        <section className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label className="text-xs">Audience</Label>
            <NativeSelect
              value={item.audience}
              disabled={!can.canEditWording || busy !== null}
              onChange={(e) => void saveField({ audience: e.target.value })}
              className="h-9"
            >
              {audiences.map((a) => (
                <option key={a.code} value={a.code}>
                  {a.label}
                </option>
              ))}
            </NativeSelect>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Call to action</Label>
            <NativeSelect
              value={item.cta ?? ''}
              disabled={!can.canEditWording || busy !== null}
              onChange={(e) => void saveField({ cta: e.target.value })}
              className="h-9"
            >
              <option value="">Not set</option>
              {ctas.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label}
                </option>
              ))}
            </NativeSelect>
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs">Hashtags on the post</Label>
            <Input
              defaultValue={hashtagText}
              disabled={!can.canEditWording || busy !== null}
              placeholder="#Fleetly #DriveWithFleetly"
              className="h-9"
              onBlur={(e) => {
                if (!can.canEditWording) return
                const tags = e.target.value.split(/[\s,]+/).filter(Boolean)
                if (tags.join(' ') === hashtagText) return
                void saveField({ hashtags: tags })
              }}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Channel</Label>
            <NativeSelect
              value={item.channel}
              disabled={!can.canEditWording || busy !== null}
              onChange={(e) => void saveField({ channel: e.target.value })}
              className="h-9"
            >
              {channels.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label}
                </option>
              ))}
            </NativeSelect>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Format</Label>
            <NativeSelect
              value={item.format}
              disabled={!can.canEditWording || busy !== null}
              onChange={(e) => void saveField({ format: e.target.value })}
              className="h-9"
            >
              {formats.map((f) => (
                <option key={f.code} value={f.code}>
                  {f.label}
                </option>
              ))}
            </NativeSelect>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Plan</Label>
            <NativeSelect
              value={item.plan_id ?? ''}
              disabled={!can.canEditNotes || busy !== null}
              onChange={(e) => void saveField({ plan_id: e.target.value })}
              className="h-9"
            >
              <option value="">Not in a plan</option>
              {plans.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title}
                </option>
              ))}
            </NativeSelect>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Goes out on</Label>
            <Input
              type="date"
              defaultValue={item.scheduled_for ?? ''}
              disabled={!can.canEditSchedule || busy !== null}
              className="h-9"
              onBlur={(e) => {
                if ((item.scheduled_for ?? '') === e.target.value) return
                void saveField({ scheduled_for: e.target.value })
              }}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Post number in the month</Label>
            <Input
              type="number"
              defaultValue={item.post_no ?? ''}
              disabled={!can.canEditNotes || busy !== null}
              className="h-9"
              onBlur={(e) => {
                if (String(item.post_no ?? '') === e.target.value) return
                void saveField({ post_no: e.target.value })
              }}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Figures come from</Label>
            {/* Free text, because the answer is a document name and the set of
                documents is not ours to enumerate. Flagged when a caption
                carries a figure and this is empty. */}
            <Input
              defaultValue={item.source_basis ?? ''}
              disabled={!can.canEditNotes || busy !== null}
              placeholder="Salary & Shift Structure"
              className="h-9"
              onBlur={(e) => {
                if ((item.source_basis ?? '') === e.target.value) return
                void saveField({ source_basis: e.target.value })
              }}
            />
            {item.figure_with_no_source && (
              <p className="text-[11px] text-amber-700 dark:text-amber-300">
                This caption carries a figure and names no source. The plan says to reproduce
                approved figures exactly, which nobody can check without knowing where they came
                from.
              </p>
            )}
          </div>
        </section>

        <section className="space-y-1.5">
          <Label className="text-xs">Why this exists, for the approver</Label>
          <Textarea
            defaultValue={item.brief ?? ''}
            rows={3}
            disabled={!can.canEditNotes || busy !== null}
            placeholder="The audience, the offer, the reason this is going out now"
            className="text-sm"
            onBlur={(e) => {
              if ((item.brief ?? '') === e.target.value) return
              void saveField({ brief: e.target.value })
            }}
          />
        </section>

        <section className="space-y-1.5">
          <Label className="text-xs">Production note, from the plan</Label>
          <Textarea
            defaultValue={item.production_note ?? ''}
            rows={2}
            disabled={!can.canEditNotes || busy !== null}
            placeholder="The guardrail in the plan's own words"
            className="text-sm"
            onBlur={(e) => {
              if ((item.production_note ?? '') === e.target.value) return
              void saveField({ production_note: e.target.value })
            }}
          />
        </section>

        <section className="space-y-2">
          <p className="text-xs font-semibold text-foreground">Every round of review</p>
          <ReviewTrail reviews={data.reviews} />
        </section>

        <section className="space-y-2">
          <p className="text-xs font-semibold text-foreground">Every edit</p>
          <EditHistory rows={data.history ?? []} />
        </section>
      </div>

      <div className="flex flex-wrap gap-2 border-t border-border px-4 py-3">
        {can.canSubmit && (
          <Button
            type="button"
            size="sm"
            variant="brand"
            className="gap-1.5"
            disabled={busy !== null}
            onClick={() =>
              void run(
                'submit',
                () => submit.trigger({ id: itemId, note: note || null }).result,
                'Sent for approval',
              )
            }
          >
            {busy === 'submit' ? <Spinner className="h-3.5 w-3.5" /> : <Send className="h-3.5 w-3.5" />}
            Send for approval
          </Button>
        )}
        {can.canPublish && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="gap-1.5"
            disabled={busy !== null}
            onClick={() =>
              void run(
                'publish',
                () => publish.trigger({ id: itemId, on_date: null }).result,
                'Recorded as published',
              )
            }
          >
            {busy === 'publish' ? <Spinner className="h-3.5 w-3.5" /> : <Megaphone className="h-3.5 w-3.5" />}
            It went out
          </Button>
        )}
        {can.canArchive && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="ml-auto gap-1.5 text-muted-foreground"
            disabled={busy !== null}
            onClick={() =>
              void run(
                'archive',
                () => archive.trigger({ id: itemId, note: note || null }).result,
                'Archived',
              )
            }
          >
            {busy === 'archive' ? <Spinner className="h-3.5 w-3.5" /> : <Archive className="h-3.5 w-3.5" />}
            Stop this one
          </Button>
        )}
      </div>
    </div>
  )
}
